import { trackException, trackTrace, trackEvent } from './applicationInsights';

export enum LogLevel {
  Debug = 0,
  Info = 1,
  Warning = 2,
  Error = 3,
  Critical = 4
}

interface LogContext {
  userId?: string;
  correlationId?: string;
  [key: string]: any;
}

class Logger {
  private name: string;
  private context: LogContext = {};

  constructor(name: string) {
    this.name = name;
  }

  setContext(context: LogContext) {
    this.context = { ...this.context, ...context };
  }

  private formatMessage(level: string, message: string): string {
    const timestamp = new Date().toISOString();
    return `[${timestamp}] [${level}] [${this.name}] ${message}`;
  }

  private getProperties(additionalProps?: any) {
    return {
      logger: this.name,
      ...this.context,
      ...additionalProps
    };
  }

  debug(message: string, properties?: any) {
    const formattedMessage = this.formatMessage('DEBUG', message);
    console.debug(formattedMessage, properties);
    trackTrace(formattedMessage, LogLevel.Debug, this.getProperties(properties));
  }

  info(message: string, properties?: any) {
    const formattedMessage = this.formatMessage('INFO', message);
    console.info(formattedMessage, properties);
    trackTrace(formattedMessage, LogLevel.Info, this.getProperties(properties));
  }

  warn(message: string, properties?: any) {
    const formattedMessage = this.formatMessage('WARN', message);
    console.warn(formattedMessage, properties);
    trackTrace(formattedMessage, LogLevel.Warning, this.getProperties(properties));
  }

  error(message: string, error?: Error | any, properties?: any) {
    const formattedMessage = this.formatMessage('ERROR', message);
    console.error(formattedMessage, error, properties);
    
    if (error instanceof Error) {
      trackException(error, LogLevel.Error, {
        message,
        ...this.getProperties(properties)
      });
    } else {
      trackTrace(formattedMessage, LogLevel.Error, {
        error: error?.toString(),
        ...this.getProperties(properties)
      });
    }
  }

  critical(message: string, error?: Error | any, properties?: any) {
    const formattedMessage = this.formatMessage('CRITICAL', message);
    console.error(formattedMessage, error, properties);
    
    if (error instanceof Error) {
      trackException(error, LogLevel.Critical, {
        message,
        ...this.getProperties(properties)
      });
    } else {
      trackTrace(formattedMessage, LogLevel.Critical, {
        error: error?.toString(),
        ...this.getProperties(properties)
      });
    }
  }

  trackEvent(eventName: string, properties?: any, measurements?: { [key: string]: number }) {
    trackEvent(eventName, this.getProperties(properties), measurements);
  }
}

// Factory function to create loggers
export function createLogger(name: string): Logger {
  return new Logger(name);
}

// Default logger instance
const defaultLogger = createLogger('default');

export default defaultLogger;